export interface AuthSession {
  sid: string
  username: string
  email?: string
}

export interface Pal {
  id: string
  username: string
}

export const MAX_PROFILE_IMAGE_BYTES = 2 * 1024 * 1024
export const PROFILE_IMAGE_TYPES = ['image/jpeg', 'image/png'] as const

export interface LoginCredentials {
  username: string
  password: string
}

export interface RegistrationDetails extends LoginCredentials {
  email: string
}

export interface AccountTrip {
  id: string
  /** Opaque bearer token extracted from the legacy private share link. */
  shareToken?: string
  ownerName: string
  description?: string
  status: 'running' | 'paused' | 'ended'
  visibility: 'private' | 'pals' | 'public' | 'unknown'
  startedAt: string
  endedAt?: string
  durationSeconds: number
  distanceKilometers: number
  averageSpeedKilometersPerHour: number
  platform?: string
  views?: number
}

export interface AccountTripUpdate {
  description: string
  visibility: 'private' | 'pals' | 'public'
}

export interface AccountTripPage {
  trips: AccountTrip[]
  hasEarlier: boolean
}

export interface AccountTripFilters {
  includeOwn: boolean
  includePals: boolean
  includePublic: boolean
  freeText: string
  fromDate: string
  toDate: string
}

export const DEFAULT_ACCOUNT_TRIP_FILTERS: Readonly<AccountTripFilters> = Object.freeze({
  includeOwn: true,
  includePals: true,
  includePublic: true,
  freeText: '',
  fromDate: '',
  toDate: '',
})

export interface AccountTripQuery {
  beforeTripId?: string
  limit?: number
  filters?: AccountTripFilters
}

export interface AccountApi {
  login(credentials: LoginCredentials, signal?: AbortSignal): Promise<AuthSession>
  validateSession(session: AuthSession, signal?: AbortSignal): Promise<AuthSession>
  listOwnTrips(session: AuthSession, query?: AccountTripQuery, signal?: AbortSignal): Promise<AccountTripPage>
  getTrip(session: AuthSession, trip: AccountTrip, signal?: AbortSignal): Promise<import('./types').TripDetails>
  /** Returns current trip metadata and only positions newer than `lastPositionId`. */
  refreshTrip(session: AuthSession, tripId: string, lastPositionId: string, signal?: AbortSignal): Promise<import('./types').TripDetails>
  /** Returns the backend-generated CSV for any trip the signed-in user may view. */
  getTripCsv(session: AuthSession, tripId: string, signal?: AbortSignal): Promise<Blob>
  /** Lists pals, or users who have added the signed-in user when `invert` is true. */
  listPals(session: AuthSession, invert?: boolean, signal?: AbortSignal): Promise<Pal[]>
  addPal(session: AuthSession, usernameOrEmail: string, signal?: AbortSignal): Promise<string | undefined>
  removePal(session: AuthSession, username: string, signal?: AbortSignal): Promise<void>
  uploadProfileImage(session: AuthSession, file: File, signal?: AbortSignal): Promise<void>
  updateTrip(session: AuthSession, tripId: string, update: AccountTripUpdate, signal?: AbortSignal): Promise<void>
  endTrip(session: AuthSession, tripId: string, signal?: AbortSignal): Promise<void>
  deleteTrip(session: AuthSession, tripId: string, signal?: AbortSignal): Promise<void>
  register(details: RegistrationDetails, signal?: AbortSignal): Promise<void>
  requestPasswordReset(usernameOrEmail: string, signal?: AbortSignal): Promise<void>
}

export type AccountApiErrorCode =
  | 'invalid-credentials'
  | 'expired-session'
  | 'network'
  | 'invalid-response'
  | 'request-rejected'

export class AccountApiError extends Error {
  constructor(
    readonly code: AccountApiErrorCode,
    message: string,
    options?: ErrorOptions,
  ) {
    super(message, options)
    this.name = 'AccountApiError'
  }
}
